import { Dispatch, ReactElement, SetStateAction, createContext, useState } from 'react';
import { TabValue } from '@/types';
import { DetailedRoute } from '@/generated/model/detailedRoute';
import { MinimalStatusCollection, MinimalTarget } from '@/generated/model';
import { useUserPreferencesState } from '@/hooks/useUserPreferencesState';

const ROUTES_ONLY_POI_NAMES_PREFERENCES_KEY = 'routes.onlyPoiNames';
const DEFAULT_ROUTES_ONLY_POI_NAMES = true;

export type RoutesContextType = {
  tab: TabValue;
  setTab: Dispatch<SetStateAction<TabValue>>;
  selectedTarget: MinimalTarget | undefined;
  setSelectedTarget: Dispatch<SetStateAction<MinimalTarget | undefined>>;
  selectedDates: { from: Date; to: Date } | undefined;
  setSelectedDates: Dispatch<SetStateAction<{ from: Date; to: Date } | undefined>>;
  routes: DetailedRoute[] | undefined;
  setRoutes: Dispatch<SetStateAction<DetailedRoute[] | undefined>>;
  positions: MinimalStatusCollection[] | undefined;
  setPositions: Dispatch<SetStateAction<MinimalStatusCollection[] | undefined>>;
  highlightedRoutes: number[] | undefined;
  setHighlightedRoutes: Dispatch<SetStateAction<number[] | undefined>>;
  searchText: string | undefined;
  setSearchText: Dispatch<SetStateAction<string | undefined>>;
  hasBeenInitialized: boolean;
  setHasBeenInitialized: Dispatch<SetStateAction<boolean>>;
  poiNamesEnabled: boolean;
  setPoiNamesEnabled: Dispatch<SetStateAction<boolean>>;
  isBottomSheetOpen: boolean;
  setIsBottomSheetOpen: Dispatch<SetStateAction<boolean>>;
  isBottomSheetUp: boolean;
  setIsBottomSheetUp: Dispatch<SetStateAction<boolean>>;
  adjustMap: boolean;
  setAdjustMap: Dispatch<SetStateAction<boolean>>;
  clickedRouteId: number;
  setClickedRouteId: Dispatch<SetStateAction<number>>;
  showSummaryTooltip: boolean;
  setShowSummaryTooltip: Dispatch<SetStateAction<boolean>>;
  routesLoading: boolean;
  setRoutesLoading: Dispatch<SetStateAction<boolean>>;
  actualToTime: Date | undefined;
  setActialToTime: Dispatch<SetStateAction<Date | undefined>>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const ctxRoutes = createContext<RoutesContextType | undefined>(undefined);

export function RoutesContext({ children }: { children: ReactElement }) {
  const [tab, setTab] = useState<TabValue>(0);
  const [selectedTarget, setSelectedTarget] = useState<MinimalTarget | undefined>(undefined);
  const [selectedDates, setSelectedDates] = useState<{ from: Date; to: Date } | undefined>(undefined);
  const [routes, setRoutes] = useState<DetailedRoute[] | undefined>(undefined);
  const [positions, setPositions] = useState<MinimalStatusCollection[] | undefined>(undefined);
  const [highlightedRoutes, setHighlightedRoutes] = useState<number[] | undefined>([]);
  const [searchText, setSearchText] = useState<string | undefined>(undefined);
  const [poiNamesEnabled, setPoiNamesEnabled] = useUserPreferencesState<boolean>(
    ROUTES_ONLY_POI_NAMES_PREFERENCES_KEY,
    DEFAULT_ROUTES_ONLY_POI_NAMES
  );
  const [hasBeenInitialized, setHasBeenInitialized] = useState<boolean>(false);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState<boolean>(false);
  const [isBottomSheetUp, setIsBottomSheetUp] = useState<boolean>(false);
  const [adjustMap, setAdjustMap] = useState<boolean>(true);
  const [clickedRouteId, setClickedRouteId] = useState<number>(-1);
  const [actualToTime, setActialToTime] = useState<Date | undefined>(undefined);
  const [showSummaryTooltip, setShowSummaryTooltip] = useState<boolean>(
    JSON.parse(sessionStorage.getItem('showRoutesSummaryTooltip') ?? 'true')
  );
  const [routesLoading, setRoutesLoading] = useState<boolean>(false);

  return (
    <ctxRoutes.Provider
      value={{
        tab,
        setTab,
        selectedTarget,
        setSelectedTarget,
        selectedDates,
        setSelectedDates,
        routes,
        setRoutes,
        positions,
        setPositions,
        highlightedRoutes,
        setHighlightedRoutes,
        searchText,
        setSearchText,
        poiNamesEnabled,
        setPoiNamesEnabled,
        hasBeenInitialized,
        setHasBeenInitialized,
        isBottomSheetOpen,
        setIsBottomSheetOpen,
        isBottomSheetUp,
        setIsBottomSheetUp,
        adjustMap,
        setAdjustMap,
        clickedRouteId,
        setClickedRouteId,
        showSummaryTooltip,
        setShowSummaryTooltip,
        routesLoading,
        setRoutesLoading,
        actualToTime,
        setActialToTime,
      }}
    >
      {children}
    </ctxRoutes.Provider>
  );
}
